<template>
  <SectionRow
    :can-edit="showEdit && canEdit"
    :show-menu="resolvedShowMenu"
    :hide-actions="hideActions"
    :show-lock="showLock"
    :no-action="noAction"
    @click="emit('navigate')"
    @menu-open="emit('menuOpen')"
    @menu-close="emit('menuClose')"
  >
    <div class="tile-main">
      <!-- Icon -->
      <div class="ios-row-icon">
        <slot name="icon">
          <div v-if="homeLogoUrl || awayLogoUrl" class="ios-row-team-logos">
            <img v-if="homeLogoUrl" :src="homeLogoUrl" alt="" class="ios-row-team-logo ios-row-team-logo--home" />
            <img v-if="awayLogoUrl" :src="awayLogoUrl" alt="" class="ios-row-team-logo ios-row-team-logo--away" />
          </div>
          <img v-else-if="leagueLogoUrl" :src="leagueLogoUrl" alt="" class="ios-row-league-logo" />
          <EventIcon v-else :type="event.type" :icon="event.icon" size="22" />
        </slot>
      </div>

      <!-- Info rows -->
      <div class="ios-row-info">
        <!-- Row 1: title -->
        <div class="ios-row-name">
          <slot name="title">
            <span class="ios-row-name-text">{{ event.title }}</span>
          </slot>
        </div>

        <!-- Row 2: date · time or date range -->
        <div class="compact-date">
          <span class="compact-date-text">{{ isMultiDay ? dateRangeText : dateText }}</span>
          <template v-if="!isMultiDay">
            <span v-if="event.timeFrom" class="ios-row-dot">
              {{ event.timeFrom }}<template v-if="event.timeTo"> – {{ event.timeTo }}</template>
            </span>
            <span v-else class="ios-row-dot">{{ $t('eventTile.allDay') }}</span>
          </template>
        </div>

        <!-- Row 3: detail (slot) -->
        <slot name="detail" />
      </div>

    </div>
    <span v-if="isCanceled" class="ios-row-canceled-mask"></span>
    <span v-if="isCanceled" class="ios-row-canceled-stamp">{{ canceledStampLabel }}</span>
    <!-- Context menu items -->
    <template #menu="{ close }">
      <div class="context-menu-item" @click="emit('navigate'); close()">
        <span>{{ $t('eventTile.detail') }}</span>
        <v-icon size="18">mdi-chevron-right</v-icon>
      </div>
      <div v-if="canEdit && showPinnedActions">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('unpinEvent', event); close()">
          <span>Odepnout</span>
          <v-icon size="18">mdi-pin-off-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('editEvent', event); close()">
          <span>{{ $t('eventTile.edit') }}</span>
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item context-menu-item--danger" @click="emit('cancelEvent', event); close()">
          <span>Zrušit</span>
          <v-icon size="18">mdi-close-circle-outline</v-icon>
        </div>
      </div>
      <div v-else-if="canEdit">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="emit('editEvent', event); close()">
          <span>{{ $t('eventTile.edit') }}</span>
          <v-icon size="18">mdi-pencil-outline</v-icon>
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item context-menu-item--danger" @click="emit('deleteEvent', event); close()">
          <span>{{ $t('eventTile.deleteEvent') }}</span>
          <v-icon size="18">mdi-trash-can-outline</v-icon>
        </div>
      </div>
    </template>
  </SectionRow>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue'
import type { IEvent } from '@/types'
import EventIcon from '@/components/EventIcon.vue'
import SectionRow from '@/components/SectionRow.vue'
import { getTeamImage } from '@/utils/imageUtils'
import { useI18n } from 'vue-i18n'

const { locale, t } = useI18n({ useScope: 'global' })

const UPLOADS = import.meta.env.VITE_UPLOADS || 'http://localhost:3000/uploads'

const props = defineProps<{
  event: IEvent
  showEdit: boolean
  canEdit: boolean
  showMenu?: boolean
  showPinnedActions?: boolean
  showLeagueLogo?: boolean
  showTeamLogos?: boolean
  hideActions?: boolean
  showLock?: boolean
  noAction?: boolean
}>()

const emit = defineEmits(['navigate', 'unpinEvent', 'deleteEvent', 'cancelEvent', 'editEvent', 'menuOpen', 'menuClose'])

const resolvedShowMenu = computed(() => props.showPinnedActions ? props.canEdit : props.showMenu)

const leagueLogoUrl = computed(() => {
  if (!props.showLeagueLogo) return null
  const img = props.event._league?.imageUrl
  return img ? `${UPLOADS}/leagues/h200-${img}` : null
})

const homeLogoUrl = computed(() => {
  if (!props.showTeamLogos) return null
  const img = props.event.meta?.homeTeam?.imageUrl
  return img ? getTeamImage(img) : null
})

const awayLogoUrl = computed(() => {
  if (!props.showTeamLogos) return null
  const img = props.event.meta?.awayTeam?.imageUrl
  return img ? getTeamImage(img) : null
})

const isCanceled = computed(() => Boolean((props.event as any)?.canceled))
const canceledStampLabel = computed(() => {
  const key = 'eventTile.canceledStamp'
  const label = t(key)
  return label === key ? 'CANCELED' : label
})

// $formatDate je globální property, přistupujeme přes proxy
const instance = getCurrentInstance()
const $formatDate = instance?.appContext.config.globalProperties.$formatDate

const dateText = computed(() => {
  if (!props.event.dateFrom) return ''
  const from = (props.event as any).dateFromRaw ?? props.event.dateFrom
  const to = (props.event as any).dateToRaw ?? props.event.dateTo
  return $formatDate ? $formatDate(from, to) : String(from)
})

const isMultiDay = computed(() => {
  const from = (props.event as any).dateFromRaw ?? props.event.dateFrom
  const to = (props.event as any).dateToRaw ?? props.event.dateTo
  if (!from || !to) return false
  const d1 = new Date(from)
  const d2 = new Date(to)
  return d1.toDateString() !== d2.toDateString()
})

const shortDay = (d: Date) => d.toLocaleDateString(locale.value, { weekday: 'short' })
const shortDate = (d: Date) => `${d.getDate()}.${d.getMonth() + 1}.`

const dateRangeText = computed(() => {
  const from = (props.event as any).dateFromRaw ?? props.event.dateFrom
  const to = (props.event as any).dateToRaw ?? props.event.dateTo
  if (!from || !to) return ''
  const d1 = new Date(from)
  const d2 = new Date(to)
  return `${shortDay(d1)} ${shortDate(d1)} – ${shortDay(d2)} ${shortDate(d2)}`
})
</script>

<style lang="scss" scoped>
.ios-row-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.tile-main {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
}

.ios-row-canceled-mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    linear-gradient(180deg, rgba(7, 10, 16, 0.16) 0%, rgba(7, 10, 16, 0.28) 100%),
    rgba(12, 16, 24, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.04),
    inset 0 0 24px rgba(0, 0, 0, 0.12);
  backdrop-filter: saturate(0.72) blur(1.2px);
  pointer-events: none;
}

.ios-row-canceled-stamp {
  position: absolute;
  right: 36px;
  top: 8px;
  z-index: 4;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(245, 124, 0, 0.98);
  background: rgba(0, 0, 0, 0.06);
  border: 3px solid rgba(245, 124, 0, 0.95);
  border-radius: 6px;
  box-shadow:
    0 6px 14px rgba(0, 0, 0, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 0 0 1px rgba(245, 124, 0, 0.28) inset;
  text-shadow:
    0 0 1px rgba(245, 124, 0, 0.55),
    0 0 0.5px rgba(245, 124, 0, 0.75);
  padding: 5px 12px 4px;
  transform: rotate(-14deg);
  transform-origin: center;
  opacity: 0.9;
  filter: saturate(0.92) contrast(1.05);
  pointer-events: none;
  backdrop-filter: blur(0.4px);
}
</style>
